import { useEffect, useState } from 'react'
import { fetchPublicIntakes, EVERGREEN_FALLBACK } from '../data/activeIntake'

/**
 * useActiveIntake — resolves the public intake picture for the marketing pages.
 *
 * Returns:
 *   {
 *     intake,        // promoted intake, EVERGREEN_FALLBACK when none, null while loading
 *     openIntakes,   // every intake currently open to applicants (server order)
 *     fallback,      // true when no promotable intake (evergreen mode)
 *     loading,       // true during the initial resolution
 *     refresh(),     // re-fetch
 *   }
 *
 * Both values come from the same single call to the public intake endpoint, so
 * a page showing several intakes costs no more requests than one showing one.
 * The result is cached at module scope for the session unless refresh() runs.
 */
let cachedPromise = null
let cachedResult = undefined // undefined = not resolved yet

const EMPTY = { intake: null, openIntakes: [] }

export function useActiveIntake() {
  const [result, setResult] = useState(cachedResult)
  const [loading, setLoading] = useState(cachedResult === undefined)

  useEffect(() => {
    let mounted = true
    if (cachedResult !== undefined) {
      setResult(cachedResult)
      setLoading(false)
      return undefined
    }
    if (!cachedPromise) {
      cachedPromise = fetchPublicIntakes()
    }
    cachedPromise
      .then((value) => {
        if (!mounted) return
        cachedResult = value || EMPTY
        setResult(cachedResult)
        setLoading(false)
      })
      .catch(() => {
        if (!mounted) return
        cachedResult = EMPTY
        setResult(EMPTY)
        setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [])

  const refresh = () => {
    cachedResult = undefined
    setLoading(true)
    cachedPromise = fetchPublicIntakes()
    cachedPromise
      .then((value) => {
        cachedResult = value || EMPTY
        setResult(cachedResult)
        setLoading(false)
      })
      .catch(() => {
        cachedResult = EMPTY
        setResult(EMPTY)
        setLoading(false)
      })
  }

  const resolvedIntake = result?.intake || null
  const openIntakes = result?.openIntakes || []

  // While loading, expose nothing (components render their own skeletons).
  // When no promotable intake, expose the evergreen fallback so the page still
  // renders a polished academy message.
  const intake = loading ? null : (resolvedIntake || EVERGREEN_FALLBACK)
  return {
    intake,
    openIntakes: loading ? [] : openIntakes,
    fallback: !loading && !resolvedIntake,
    loading,
    refresh,
  }
}
